import type { WeatherDay } from '../../config/contracts';
import type { WeatherUnits } from '../../config/env';
import { clsx } from '../../components/clsx';
import { formatShortDay, parseLocalDateKey } from '../../lib/date';

/**
 * Five days as five COLUMNS, not five rows.
 *
 * The card is ~350x330 usable at its smallest measured size (369x386 at
 * 1536x864). Five stacked rows plus a conditions hero does not fit; five
 * ~66px columns pinned to the bottom of the body does, with room to spare.
 */
export function ForecastStrip({
  days,
  units,
  todayKey,
}: {
  days: readonly WeatherDay[];
  units: WeatherUnits;
  todayKey: string;
}) {
  if (days.length === 0) return null;
  const metric = units === 'metric';

  // open-meteo returns the LOCATION's calendar days (timezone=auto), which can
  // differ from the browser's day for a location in another timezone. Falling
  // back to column 0 keeps exactly one "Now" column highlighted either way.
  const highlight = days.some((d) => d.dateKey === todayKey) ? todayKey : days[0]?.dateKey;

  return (
    <div className="grid grid-cols-5 gap-1" data-testid="weather-forecast">
      {days.slice(0, 5).map((day) => {
        const isToday = day.dateKey === highlight;
        return (
          <div
            key={day.dateKey}
            className={clsx(
              'flex flex-col items-center gap-0.5 rounded-lg px-0.5 py-1.5',
              isToday ? 'bg-panel-raised' : 'bg-transparent',
            )}
          >
            <span
              className={clsx(
                'text-[10px] font-semibold uppercase tracking-wide',
                isToday ? 'text-ink' : 'text-ink-subtle',
              )}
            >
              {dayLabel(day.dateKey, isToday)}
            </span>
            <span className="text-base leading-none" aria-hidden>
              {day.icon}
            </span>
            <span className="text-[12px] font-semibold tabular-nums text-ink">
              {Math.round(metric ? day.highC : day.highF)}°
            </span>
            <span className="text-[11px] tabular-nums text-ink-subtle">
              {Math.round(metric ? day.lowC : day.lowF)}°
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Never `new Date('YYYY-MM-DD')` — that is UTC midnight, a day early here. */
function dayLabel(dateKey: string, isToday: boolean): string {
  if (isToday) return 'Now';
  const parsed = parseLocalDateKey(dateKey);
  return parsed === null ? '—' : formatShortDay(parsed);
}
